// HubSpot (support chat widget + cookie banner) lifecycle, kept free of React
// so the suppress → restore sequence can be unit-tested with a fake
// window/document (web.app's vitest runs in plain node, no DOM).
//
// Two independent pieces:
// - suppressHubSpot(): while a suppressed route is showing, remove the chat
//   widget and take the cookie banner out of the page. Returns a restore
//   function that puts the banner back.
// - startHubSpotChat(): load the chat widget (identified when logged in).
//   Returns a cleanup that cancels a pending identification and removes the
//   widget, so a later run can load it again with widget.load().

declare global {
  interface Window {
    HubSpotConversations?: {
      widget: {
        load: (options?: { widgetOpen?: boolean }) => void;
        open: () => void;
        close: () => void;
        remove: () => void;
        refresh: (openToNewThread?: boolean) => void;
        status: () => { loaded: boolean; pending: boolean };
      };
      on: (event: string, callback: () => void) => void;
      off: (event: string, callback: () => void) => void;
    };
    hsConversationsOnReady?: Array<() => void>;
    hsConversationsSettings?: {
      loadImmediately?: boolean;
      identificationEmail?: string;
      identificationToken?: string;
    };
  }
}

export interface HubSpotEnv {
  window: Window;
  document: Document;
  MutationObserver: typeof MutationObserver;
}

// Injected by HubSpot's loader (hs-banner.com banner.js), not by us.
export const HUBSPOT_COOKIE_BANNER_ID = "hs-eu-cookie-confirmation";

/**
 * Hide HubSpot while a suppressed route is showing.
 *
 * The cookie banner is detached rather than destroyed: banner.js runs once
 * per page load and its child <script> stays in the document, so re-adding
 * the loader would not recreate a destroyed banner. Re-attaching the same
 * node keeps its consent handlers wired, and a visitor who had not answered
 * yet gets the banner back on the next normal page. A banner that arrives
 * while suppressed (banner.js already in flight) is detached the same way.
 *
 * The loader <script> is left in place: removing a script element does not
 * unload what it already ran, and keeping it means re-enabling does not
 * re-run the loader.
 */
export function suppressHubSpot(env: HubSpotEnv): () => void {
  const { window: win, document: doc } = env;

  win.HubSpotConversations?.widget.remove();
  win.hsConversationsOnReady = [];
  // Stops conversations-embed.js from auto-loading the widget if it is still
  // in flight; startHubSpotChat sets the right value again on restore.
  win.hsConversationsSettings = { loadImmediately: false };

  let stashed: { banner: Element; parent: Node | null } | null = null;
  const stashBanner = () => {
    const banner = doc.getElementById(HUBSPOT_COOKIE_BANNER_ID);
    if (!banner) return;
    stashed = { banner, parent: banner.parentNode };
    banner.remove();
  };

  stashBanner();
  const observer = new env.MutationObserver(stashBanner);
  observer.observe(doc.body, { childList: true, subtree: true });

  return () => {
    observer.disconnect();
    if (!stashed || doc.getElementById(HUBSPOT_COOKIE_BANNER_ID)) return;
    const { banner, parent } = stashed;
    const target = parent?.isConnected ? parent : doc.body;
    target.appendChild(banner);
    stashed = null;
  };
}

export interface StartHubSpotChatOptions {
  isAuthenticated: boolean;
  email?: string | null;
  getVisitorToken: () => Promise<string | null | undefined>;
  /** Returns true (once) if open() was called before the widget existed. */
  takePendingOpen: () => boolean;
}

export function startHubSpotChat(
  env: HubSpotEnv,
  {
    isAuthenticated,
    email,
    getVisitorToken,
    takePendingOpen,
  }: StartHubSpotChatOptions,
): () => void {
  const win = env.window;
  let cancelled = false;

  const loadWidget = () => {
    if (cancelled) return;
    const conversations = win.HubSpotConversations;
    if (!conversations) return;
    // Needed after a widget.remove() (suppressed route, logout): HubSpot only
    // brings a removed widget back on refresh or an explicit load().
    conversations.widget.load();
    if (takePendingOpen()) conversations.widget.open();
  };

  const cleanup = () => {
    cancelled = true;
    win.hsConversationsOnReady = (win.hsConversationsOnReady ?? []).filter(
      (callback) => callback !== loadWidget,
    );
    win.HubSpotConversations?.widget.remove();
  };

  if (!isAuthenticated) {
    // Drops any identification left over from a logged-in session.
    win.hsConversationsSettings = { loadImmediately: true };
    // If the SDK is not here yet, loadImmediately makes it load on arrival.
    loadWidget();
    return cleanup;
  }

  const identify = async () => {
    try {
      const token = await getVisitorToken();
      if (cancelled) return;
      if (token) {
        win.hsConversationsSettings = {
          ...win.hsConversationsSettings,
          identificationEmail: email || undefined,
          identificationToken: token,
        };
      }
    } catch {
      // Fall through to load widget without identification
    }
    if (cancelled) return;

    if (win.HubSpotConversations) {
      loadWidget();
    } else {
      win.hsConversationsOnReady = [
        ...(win.hsConversationsOnReady ?? []),
        loadWidget,
      ];
    }
  };

  void identify();
  return cleanup;
}
